import { SSO_AUTH_MODE } from '@core/runtime-config'
import { AdminRoute } from '@feature/authentication/components/AdminRoute'
import { ManagerRoute } from '@feature/authentication/components/ManagerRoute'
import { ProtectedRoute } from '@feature/authentication/components/ProtectedRoute'
import { TenantBranding } from '@feature/branding/views/TenantBranding/TenantBranding'
import { LoadingState } from '@shared/components/feedback/LoadingState'
import { NotFoundState } from '@shared/components/feedback/NotFoundState'
import { RouteErrorState } from '@shared/components/feedback/RouteErrorState'
import { AppLayout } from '@shared/components/layout/AppLayout'
import { createBrowserRouter, Navigate, Outlet } from 'react-router'

// Lazy loading das features
const LoginPage = () => import('@feature/authentication/views/Login/Login')
const InviteContinuationPage = () =>
  import('@feature/authentication/views/InviteContinuation/InviteContinuation')
const InviteResultPage = () =>
  import('@feature/authentication/views/InviteContinuation/InviteResult')
const ForgotPasswordPage = () =>
  import('@feature/authentication/views/ForgotPassword').then((m) => ({
    Component: m.ForgotPassword,
  }))
const ResetPasswordPage = () =>
  import('@feature/authentication/views/ResetPassword').then((m) => ({
    Component: m.ResetPassword,
  }))
const ProfilePage = () =>
  import('@feature/users/pages/Profile').then((m) => ({ Component: m.Profile }))
const UserListPage = () =>
  import('@feature/users/pages/UserList').then((m) => ({ Component: m.UserList }))
const UserCreatePage = () =>
  import('@feature/users/pages/UserCreate').then((m) => ({ Component: m.UserCreate }))
const UserEditPage = () =>
  import('@feature/users/pages/UserEdit').then((m) => ({ Component: m.UserEdit }))
const DashboardPage = () => import('@feature/dashboard/views/Dashboard')
const CommunicationsListPage = () =>
  import('@feature/communications/views/CommunicationsList/ReaderAwareList')
const CommunicationDetailPage = () =>
  import('@feature/communications/views/CommunicationDetail/CommunicationDetail')
const CommunicationCreatePage = () =>
  import('@feature/communications/views/CommunicationCreate/CommunicationCreate')
const CommunicationEditPage = () =>
  import('@feature/communications/views/CommunicationEdit/CommunicationEdit')
const DepartmentsListPage = () =>
  import('@feature/departments/views/DepartmentsList/DepartmentsList')
const DepartmentDetailPage = () =>
  import('@feature/departments/views/DepartmentDetail/DepartmentDetail')
const DepartmentCreatePage = () =>
  import('@feature/departments/views/DepartmentCreate/DepartmentCreate')
const DepartmentEditPage = () => import('@feature/departments/views/DepartmentEdit/DepartmentEdit')
const AnalyticsPage = () => import('@feature/analytics/views/Analytics/Analytics')
const NotificationsPage = () => import('@feature/notifications/views/Notifications/Notifications')
const StoresListPage = () => import('@feature/stores/views/StoresList/StoresList')
const StoreDetailPage = () => import('@feature/stores/views/StoreDetail/StoreDetail')
const StoreCreatePage = () => import('@feature/stores/views/StoreCreate/StoreCreate')
const StoreEditPage = () => import('@feature/stores/views/StoreEdit/StoreEdit')
const BusinessHoursListPage = () =>
  import('@feature/business-hours/views/BusinessHoursList/BusinessHoursList')
const BusinessHoursCreatePage = () =>
  import('@feature/business-hours/views/BusinessHoursCreate/BusinessHoursCreate')
const BusinessHoursEditPage = () =>
  import('@feature/business-hours/views/BusinessHoursEdit/BusinessHoursEdit')
const AuditLogPage = () => import('@feature/audit/views/AuditLog/AuditLog')
const SearchResultsPage = () => import('@feature/search/views/SearchResults/SearchResults')

const hydrateFallbackElement = <LoadingState message="Carregando página..." />

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
    errorElement: <RouteErrorState />,
  },
  {
    path: '/login',
    lazy: LoginPage,
    hydrateFallbackElement,
    errorElement: <RouteErrorState />,
  },
  {
    path: '/acesso/convite',
    lazy: InviteContinuationPage,
    hydrateFallbackElement,
    errorElement: <RouteErrorState />,
  },
  {
    path: '/acesso/convite/resultado',
    lazy: InviteResultPage,
    hydrateFallbackElement,
    errorElement: <RouteErrorState />,
  },
  {
    path: '/forgot-password',
    element: SSO_AUTH_MODE ? <Navigate to="/login" replace /> : undefined,
    lazy: SSO_AUTH_MODE ? undefined : ForgotPasswordPage,
    hydrateFallbackElement,
    errorElement: <RouteErrorState />,
  },
  {
    path: '/accept-invitation',
    element: SSO_AUTH_MODE ? <Navigate to="/login" replace /> : undefined,
    lazy: SSO_AUTH_MODE
      ? undefined
      : async () => ({
          Component: (await import('@feature/authentication/views/ResetPassword')).AcceptInvitation,
        }),
    hydrateFallbackElement,
    errorElement: <RouteErrorState />,
  },
  {
    path: '/reset-password',
    element: SSO_AUTH_MODE ? <Navigate to="/login" replace /> : undefined,
    lazy: SSO_AUTH_MODE ? undefined : ResetPasswordPage,
    hydrateFallbackElement,
    errorElement: <RouteErrorState />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    hydrateFallbackElement,
    errorElement: <RouteErrorState />,
    children: [
      {
        path: 'settings',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        children: [{ index: true, lazy: () => import('@feature/settings/SettingsPage') }],
        handle: { breadcrumb: 'Configurações' },
      },
      {
        path: 'calendar',
        lazy: () => import('@feature/calendar/CalendarPage'),
        handle: { breadcrumb: 'Calendário' },
      },
      {
        path: 'calendar/:id',
        lazy: () => import('@feature/calendar/EventDetail'),
        handle: { breadcrumb: 'Evento' },
      },
      {
        path: 'calendar',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        children: [
          {
            path: 'create',
            lazy: () => import('@feature/calendar/EventForm'),
            handle: { breadcrumb: 'Novo evento' },
          },
          {
            path: ':id/edit',
            lazy: () => import('@feature/calendar/EventForm'),
            handle: { breadcrumb: 'Editar evento' },
          },
          {
            path: 'editorial',
            lazy: () => import('@feature/calendar/EditorialPage'),
            handle: { breadcrumb: 'Calendário editorial' },
          },
        ],
      },
      {
        path: 'board',
        lazy: () => import('@feature/communications/views/NoticeBoard/NoticeBoard'),
        handle: { breadcrumb: 'Mural de recados' },
      },
      {
        path: 'communication-templates',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        children: [
          { index: true, lazy: () => import('@feature/communications/templates/Templates') },
        ],
        handle: { breadcrumb: 'Modelos de comunicado' },
      },
      {
        path: 'operations',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        children: [{ index: true, lazy: () => import('@feature/operations/Operations') }],
        handle: { breadcrumb: 'Operação' },
      },
      {
        path: 'whatsapp',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        children: [
          { index: true, lazy: () => import('@feature/whatsapp/WhatsAppPage') },
          {
            path: ':source/:id',
            lazy: () => import('@feature/whatsapp/WhatsAppDetail'),
            handle: { breadcrumb: 'Detalhes do envio' },
          },
        ],
        handle: { breadcrumb: 'Envios do WhatsApp' },
      },
      {
        path: 'readership',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        children: [
          { index: true, lazy: () => import('@feature/analytics/views/Readership/Readership') },
        ],
        handle: { breadcrumb: 'Painel de leituras' },
      },
      {
        path: 'dashboard',
        lazy: DashboardPage,
        handle: { breadcrumb: 'Início' },
      },
      {
        path: 'profile',
        lazy: ProfilePage,
        handle: { breadcrumb: 'Meu Perfil' },
      },
      {
        path: 'search',
        lazy: SearchResultsPage,
        handle: { breadcrumb: 'Busca' },
      },
      {
        path: 'users',
        element: (
          <AdminRoute>
            <Outlet />
          </AdminRoute>
        ),
        handle: { breadcrumb: 'Usuários' },
        children: [
          {
            index: true,
            lazy: UserListPage,
          },
          {
            path: 'create',
            lazy: UserCreatePage,
            handle: { breadcrumb: 'Novo Usuário' },
          },
          {
            path: ':id/edit',
            lazy: UserEditPage,
            handle: { breadcrumb: 'Editar' },
          },
        ],
      },
      {
        path: 'communications',
        handle: { breadcrumb: 'Comunicados' },
        children: [
          {
            index: true,
            lazy: CommunicationsListPage,
          },
          {
            path: 'create',
            element: (
              <ManagerRoute>
                <Outlet />
              </ManagerRoute>
            ),
            children: [{ index: true, lazy: CommunicationCreatePage }],
            handle: { breadcrumb: 'Novo Comunicado' },
          },
          {
            path: ':id',
            lazy: CommunicationDetailPage,
            handle: { breadcrumb: 'Detalhes' },
          },
          {
            path: ':id/edit',
            element: (
              <ManagerRoute>
                <Outlet />
              </ManagerRoute>
            ),
            children: [{ index: true, lazy: CommunicationEditPage }],
            handle: { breadcrumb: 'Editar' },
          },
        ],
      },
      {
        path: 'departments',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        handle: { breadcrumb: 'Departamentos' },
        children: [
          {
            index: true,
            lazy: DepartmentsListPage,
          },
          {
            path: 'create',
            lazy: DepartmentCreatePage,
            handle: { breadcrumb: 'Novo Departamento' },
          },
          {
            path: ':id',
            lazy: DepartmentDetailPage,
            handle: { breadcrumb: 'Detalhes' },
          },
          {
            path: ':id/edit',
            lazy: DepartmentEditPage,
            handle: { breadcrumb: 'Editar' },
          },
        ],
      },
      {
        path: 'stores',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        handle: { breadcrumb: 'Lojas' },
        children: [
          {
            index: true,
            lazy: StoresListPage,
          },
          {
            path: 'create',
            lazy: StoreCreatePage,
            handle: { breadcrumb: 'Nova Loja' },
          },
          {
            path: ':uuid',
            lazy: StoreDetailPage,
            handle: { breadcrumb: 'Detalhes' },
          },
          {
            path: ':uuid/edit',
            lazy: StoreEditPage,
            handle: { breadcrumb: 'Editar' },
          },
        ],
      },
      {
        path: 'business-hours',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        handle: { breadcrumb: 'Horários Comerciais' },
        children: [
          {
            index: true,
            lazy: BusinessHoursListPage,
          },
          {
            path: 'create',
            lazy: BusinessHoursCreatePage,
            handle: { breadcrumb: 'Novo Horário' },
          },
          {
            path: ':uuid/edit',
            lazy: BusinessHoursEditPage,
            handle: { breadcrumb: 'Editar' },
          },
        ],
      },
      {
        path: 'audit',
        element: (
          <AdminRoute>
            <Outlet />
          </AdminRoute>
        ),
        children: [{ index: true, lazy: AuditLogPage }],
        handle: { breadcrumb: 'Auditoria' },
      },
      {
        path: 'analytics',
        element: (
          <ManagerRoute>
            <Outlet />
          </ManagerRoute>
        ),
        handle: { breadcrumb: 'Relatórios' },
        children: [
          {
            index: true,
            lazy: AnalyticsPage,
          },
        ],
      },
      {
        path: 'tenant-smtp',
        element: (
          <AdminRoute>
            <Outlet />
          </AdminRoute>
        ),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('@feature/branding/views/TenantSmtp/TenantSmtp')).TenantSmtp,
            }),
          },
        ],
        handle: { breadcrumb: 'Configuração de e-mail' },
      },
      {
        path: 'tenant-timezone',
        element: (
          <AdminRoute>
            <Outlet />
          </AdminRoute>
        ),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('@feature/branding/views/TenantTimezone/TenantTimezone'))
                .TenantTimezone,
            }),
          },
        ],
        handle: { breadcrumb: 'Fuso horário' },
      },
      {
        path: 'tenant-branding',
        element: (
          <AdminRoute>
            <TenantBranding />
          </AdminRoute>
        ),
        handle: { breadcrumb: 'Identidade visual' },
      },
      {
        path: 'notifications',
        lazy: NotificationsPage,
        handle: { breadcrumb: 'Notificações' },
      },
      {
        path: '*',
        element: (
          <NotFoundState
            message="Página não encontrada."
            backTo="/dashboard"
            backLabel="Ir ao dashboard"
          />
        ),
      },
    ],
  },
])
